import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import { NriMatrixTable } from './nri-matrix-table';
import styles from './uat-nri-matrix-popup.module.scss';
import {
  nriCalculationIdForMatrixAtom,
  nriMatrixLoadableAtom,
} from 'atoms/nri-matrix-atoms';
import type { NriMatrixRow, NriMatrixResponse } from 'types/api-responses';
import { CalculationType } from 'components/nri-table/types';
import { highlightGisCounterpart } from './utils';

interface UATNRIMatrixPopupProps {
  nriCalculationId: number;
  calculationType?: CalculationType;
  /** Opened from a GIS NRI / GIS WI value: highlight the GIS row that value
   *  comes from instead of the in-use row. */
  highlightGisRow?: boolean;
}

export const UATNRIMatrixPopup = ({
  nriCalculationId,
  calculationType = 'NRI',
  highlightGisRow = false,
}: UATNRIMatrixPopupProps) => {
  const setNriCalculationId = useSetAtom(nriCalculationIdForMatrixAtom);
  const nriMatrixLoadable = useAtomValue(nriMatrixLoadableAtom);

  useEffect(() => {
    setNriCalculationId(nriCalculationId);
    return () => {
      setNriCalculationId(null);
    };
  }, [nriCalculationId, setNriCalculationId]);

  const matrixData: NriMatrixResponse | null = useMemo(() => {
    if (nriMatrixLoadable.state === 'hasData' && nriMatrixLoadable.data) {
      return nriMatrixLoadable.data;
    }
    return null;
  }, [nriMatrixLoadable]);

  const isLoading = nriMatrixLoadable.state === 'loading';
  const hasError = nriMatrixLoadable.state === 'hasError';

  const matrixRows: NriMatrixRow[] = useMemo(() => {
    if (!matrixData || !matrixData.rows) return [];

    const rows = matrixData.rows.map((apiRow) => ({
      inUse: apiRow.inUse,
      allocationMethod: apiRow.allocationMethod,
      source: apiRow.source,
      uat: matrixData.unitAllocationTract?.tractName ?? '--',
      value: apiRow.value,
      factor: apiRow.factor,
      uatNri: apiRow.nri ?? null,
      uatWi: apiRow.workingInterest ?? null,
    }));
    return highlightGisRow ? highlightGisCounterpart(rows) : rows;
  }, [matrixData, highlightGisRow]);

  const slotName = matrixData
    ? (matrixData.wellName ?? matrixData.uwi ?? matrixData.slotId ?? '--')
    : '--';

  const titlePrefix = calculationType === 'WI' ? 'WI Matrix' : 'NRI Matrix';

  if (isLoading) {
    return (
      <div data-portal-container>
        <div className={styles.title}>
          {titlePrefix}
          <span className={styles.subtitle}>({slotName})</span>
        </div>
        <div className={styles.tableContainer}>Loading...</div>
      </div>
    );
  }

  if (hasError || !matrixData) {
    return (
      <div data-portal-container>
        <div className={styles.title}>
          {titlePrefix}
          <span className={styles.subtitle}>({slotName})</span>
        </div>
        <div className={styles.tableContainer}>
          Error loading {calculationType} Matrix data
        </div>
      </div>
    );
  }

  return (
    <div data-portal-container>
      <div className={styles.title}>
        {titlePrefix}
        <span className={styles.subtitle}>({slotName})</span>
      </div>
      <div className={styles.tableContainer}>
        <NriMatrixTable rows={matrixRows} calculationType={calculationType} />
      </div>
    </div>
  );
};
